import type { Pagina, BlocoConteudo, RegistroAuditoria, CmsPublicadoPayload } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { CmsApi } from './cms.api.js';
import { formatarDataHora } from '../pedidos/formatacao.js';

interface PermissoesEdicao {
  atualizar: boolean;
}

/**
 * `<erp-pagina-editor-modal>` — edita o rascunho de uma página do CMS,
 * publica/despublica, e lista/restaura versões antigas (Sprint 11).
 * "Versões" nunca é uma tabela própria — é `RegistroAuditoria[]`
 * (`GET /cms/paginas/:id/versoes`, filtrado a `cms.publicado` no
 * backend), mesma extensão do fluxo de auditoria universal descrita em
 * `PRODUCT.md` 3.9/3.11.
 */
export class ErpPaginaEditorModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _cmsApi: CmsApi | null = null;
  private _permissoes: PermissoesEdicao = { atualizar: false };
  private _aoSalvar: (() => void) | null = null;

  private aberto = false;
  private pagina: Pagina | null = null;
  private versoes: RegistroAuditoria[] = [];
  private blocosEdicao: BlocoConteudo[] = [];
  private novoTipoBloco: BlocoConteudo['tipo'] = 'texto';
  private enviando = false;
  private mensagemErro = '';
  private mensagemSucesso = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set cmsApi(valor: CmsApi) {
    this._cmsApi = valor;
  }

  set permissoes(valor: PermissoesEdicao) {
    this._permissoes = valor;
  }

  set aoSalvar(valor: () => void) {
    this._aoSalvar = valor;
  }

  async abrir(pagina: Pagina): Promise<void> {
    this.pagina = pagina;
    this.blocosEdicao = pagina.conteudoRascunho.blocos.map((b) => ({ ...b }));
    this.mensagemErro = '';
    this.mensagemSucesso = '';
    this.aberto = true;
    this.versoes = [];
    this.render();
    await this.carregarVersoes();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async carregarVersoes(): Promise<void> {
    if (!this._cmsApi || !this.pagina) return;
    try {
      this.versoes = await this._cmsApi.listarVersoes(this.pagina.id);
    } catch {
      this.versoes = [];
    }
    this.render();
  }

  private async recarregarPagina(): Promise<void> {
    if (!this._cmsApi || !this.pagina) return;
    this.pagina = await this._cmsApi.obterPorId(this.pagina.id);
    this._aoSalvar?.();
  }

  private lerCamposForm(): { titulo: string; seoTitulo: string; seoDescricao: string } {
    return {
      titulo: this.root.querySelector<HTMLInputElement>('#titulo')?.value ?? '',
      seoTitulo: this.root.querySelector<HTMLInputElement>('#seo-titulo')?.value ?? '',
      seoDescricao: this.root.querySelector<HTMLTextAreaElement>('#seo-descricao')?.value ?? '',
    };
  }

  private lerBlocosDoForm(): void {
    this.blocosEdicao = this.blocosEdicao.map((bloco, index) => {
      if (bloco.tipo === 'texto') {
        const texto = this.root.querySelector<HTMLTextAreaElement>(`[data-bloco-texto="${index}"]`)?.value ?? '';
        return { tipo: 'texto' as const, dados: { texto } };
      }
      const arquivoId = this.root.querySelector<HTMLInputElement>(`[data-bloco-arquivo="${index}"]`)?.value ?? '';
      const legenda = this.root.querySelector<HTMLInputElement>(`[data-bloco-legenda="${index}"]`)?.value ?? '';
      return { tipo: 'imagem' as const, dados: { arquivoId, ...(legenda ? { legenda } : {}) } };
    });
  }

  private async salvarRascunho(): Promise<void> {
    if (!this._cmsApi || !this.pagina || this.enviando) return;
    this.lerBlocosDoForm();
    const { titulo, seoTitulo, seoDescricao } = this.lerCamposForm();

    this.enviando = true;
    this.mensagemErro = '';
    this.mensagemSucesso = '';
    this.render();

    try {
      await this._cmsApi.atualizarRascunho(this.pagina.id, {
        titulo,
        conteudo: { blocos: this.blocosEdicao },
        seoTitulo: seoTitulo || null,
        seoDescricao: seoDescricao || null,
      });
      this.mensagemSucesso = 'rascunho salvo';
      await this.recarregarPagina();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar o rascunho.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private async publicar(): Promise<void> {
    if (!this._cmsApi || !this.pagina || this.enviando) return;
    this.enviando = true;
    this.mensagemErro = '';
    this.render();
    try {
      await this._cmsApi.publicar(this.pagina.id);
      this.mensagemSucesso = 'página publicada';
      await this.recarregarPagina();
      await this.carregarVersoes();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível publicar.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private async despublicar(): Promise<void> {
    if (!this._cmsApi || !this.pagina || this.enviando) return;
    this.enviando = true;
    this.mensagemErro = '';
    this.render();
    try {
      await this._cmsApi.despublicar(this.pagina.id);
      this.mensagemSucesso = 'página despublicada';
      await this.recarregarPagina();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível despublicar.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private async restaurar(versaoId: string): Promise<void> {
    if (!this._cmsApi || !this.pagina || this.enviando) return;
    this.enviando = true;
    this.mensagemErro = '';
    this.render();
    try {
      const restaurada = await this._cmsApi.restaurar(this.pagina.id, versaoId);
      this.pagina = restaurada;
      this.blocosEdicao = restaurada.conteudoRascunho.blocos.map((b) => ({ ...b }));
      this.mensagemSucesso = 'versão restaurada';
      this._aoSalvar?.();
      await this.carregarVersoes();
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível restaurar esta versão.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private adicionarBloco(): void {
    this.lerBlocosDoForm();
    const novoBloco: BlocoConteudo =
      this.novoTipoBloco === 'texto' ? { tipo: 'texto', dados: { texto: '' } } : { tipo: 'imagem', dados: { arquivoId: '' } };
    this.blocosEdicao = [...this.blocosEdicao, novoBloco];
    this.render();
  }

  private removerBloco(index: number): void {
    this.lerBlocosDoForm();
    this.blocosEdicao = this.blocosEdicao.filter((_, i) => i !== index);
    this.render();
  }

  private moverBloco(index: number, direcao: -1 | 1): void {
    this.lerBlocosDoForm();
    const alvo = index + direcao;
    if (alvo < 0 || alvo >= this.blocosEdicao.length) return;
    const copia = [...this.blocosEdicao];
    [copia[index], copia[alvo]] = [copia[alvo]!, copia[index]!];
    this.blocosEdicao = copia;
    this.render();
  }

  private render(): void {
    if (!this.pagina) {
      this.root.innerHTML = '';
      return;
    }
    const pagina = this.pagina;
    const podeEditar = this._permissoes.atualizar && !this.enviando;

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); max-height: 70vh; overflow-y: auto; padding-right: var(--pg-space-2, 8px); }
        ${FORM_FIELD_CSS}
        textarea { resize: vertical; }
        h3 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: var(--pg-space-2, 8px) 0 0; }
        .bloco { border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); border-radius: var(--pg-radius-md, 10px); padding: var(--pg-space-3, 12px); display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); }
        .bloco-topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-2, 8px); }
        .bloco-tipo { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); text-transform: uppercase; }
        .bloco-acoes { display: flex; gap: var(--pg-space-2, 8px); }
        .bloco-acoes button { background: none; border: none; color: var(--pg-color-texto-secundario, #9aa1ab); cursor: pointer; font-size: var(--pg-font-size-xs, 0.75rem); }
        .bloco-acoes button:disabled { opacity: 0.4; cursor: default; }
        .adicionar-bloco { display: flex; gap: var(--pg-space-3, 12px); align-items: center; }
        .versoes { display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); }
        .versao-item { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-3, 12px); font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .versao-item button { background: none; border: none; color: var(--pg-color-destaque, #2f8cff); cursor: pointer; font-size: var(--pg-font-size-xs, 0.75rem); }
        .status-linha { display: flex; align-items: center; gap: var(--pg-space-3, 12px); }
        .erro { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-erro, #e5484d); }
        .sucesso { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-sucesso, #3bb273); }
        .erro:empty, .sucesso:empty { display: none; }
        .rodape { display: flex; justify-content: space-between; gap: var(--pg-space-3, 12px); flex-wrap: wrap; }
        .rodape-acoes { display: flex; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Editar página — ${escaparHtml(pagina.titulo)}</span>
        <form novalidate>
          <div class="status-linha">
            <pg-badge tone="${pagina.conteudoPublicado ? 'sucesso' : 'neutro'}">${pagina.conteudoPublicado ? 'Publicada' : 'Nunca publicada'}</pg-badge>
            ${pagina.possuiAlteracoesPendentes ? '<pg-badge tone="alerta">Alterações não publicadas</pg-badge>' : ''}
          </div>

          <label>Título <input id="titulo" type="text" maxlength="200" value="${escaparAtributo(pagina.titulo)}" ${podeEditar ? '' : 'disabled'} /></label>
          <label>SEO — título <input id="seo-titulo" type="text" maxlength="200" value="${escaparAtributo(pagina.seoTitulo ?? '')}" ${podeEditar ? '' : 'disabled'} /></label>
          <label>SEO — descrição <textarea id="seo-descricao" rows="2" maxlength="500" ${podeEditar ? '' : 'disabled'}>${escaparHtml(pagina.seoDescricao ?? '')}</textarea></label>

          <h3>Blocos de conteúdo</h3>
          ${this.blocosEdicao.map((bloco, index) => this.renderBloco(bloco, index, podeEditar)).join('') || '<p class="erro">nenhum bloco ainda</p>'}
          ${
            podeEditar
              ? `<div class="adicionar-bloco">
                   <select id="novo-tipo-bloco">
                     <option value="texto" ${this.novoTipoBloco === 'texto' ? 'selected' : ''}>Texto</option>
                     <option value="imagem" ${this.novoTipoBloco === 'imagem' ? 'selected' : ''}>Imagem</option>
                   </select>
                   <pg-button id="botao-adicionar-bloco" variant="ghost">Adicionar bloco</pg-button>
                 </div>`
              : ''
          }

          <h3>Versões publicadas</h3>
          <div class="versoes">
            ${
              this.versoes.length === 0
                ? '<p class="bloco-tipo">nenhuma versão publicada ainda</p>'
                : this.versoes
                    .map(
                      (v) => `
                <div class="versao-item">
                  <span>v${(v.depois as unknown as CmsPublicadoPayload)?.versao ?? '?'} — ${formatarDataHora(v.criadoEm)}</span>
                  ${podeEditar ? `<button type="button" data-restaurar="${v.id}">Restaurar</button>` : ''}
                </div>`
                    )
                    .join('')
            }
          </div>

          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
          <span class="sucesso">${escaparHtml(this.mensagemSucesso)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-fechar" variant="ghost">Fechar</pg-button>
          <div class="rodape-acoes">
            ${podeEditar && pagina.conteudoPublicado ? '<pg-button id="botao-despublicar" variant="ghost">Despublicar</pg-button>' : ''}
            ${podeEditar ? '<pg-button id="botao-salvar" variant="secondary">Salvar rascunho</pg-button>' : ''}
            ${podeEditar ? '<pg-button id="botao-publicar" variant="primary">Publicar</pg-button>' : ''}
          </div>
        </div>
      </pg-modal>
    `;

    this.wireEvents();
  }

  private renderBloco(bloco: BlocoConteudo, index: number, podeEditar: boolean): string {
    const campos =
      bloco.tipo === 'texto'
        ? `<textarea data-bloco-texto="${index}" rows="3" ${podeEditar ? '' : 'disabled'}>${escaparHtml(bloco.dados.texto)}</textarea>`
        : `<input data-bloco-arquivo="${index}" type="text" placeholder="id do arquivo (Storage)" value="${escaparAtributo(bloco.dados.arquivoId)}" ${podeEditar ? '' : 'disabled'} />
           <input data-bloco-legenda="${index}" type="text" placeholder="legenda (opcional)" value="${escaparAtributo(bloco.dados.legenda ?? '')}" ${podeEditar ? '' : 'disabled'} />`;

    return `
      <div class="bloco">
        <div class="bloco-topo">
          <span class="bloco-tipo">${bloco.tipo}</span>
          ${
            podeEditar
              ? `<div class="bloco-acoes">
                   <button type="button" data-mover-cima="${index}" ${index === 0 ? 'disabled' : ''}>↑</button>
                   <button type="button" data-mover-baixo="${index}" ${index === this.blocosEdicao.length - 1 ? 'disabled' : ''}>↓</button>
                   <button type="button" data-remover-bloco="${index}">Remover</button>
                 </div>`
              : ''
          }
        </div>
        ${campos}
      </div>`;
  }

  private wireEvents(): void {
    this.root.querySelector('#botao-fechar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', () => void this.salvarRascunho());
    this.root.querySelector('#botao-publicar')?.addEventListener('pg-click', () => void this.publicar());
    this.root.querySelector('#botao-despublicar')?.addEventListener('pg-click', () => void this.despublicar());
    this.root.querySelector('#botao-adicionar-bloco')?.addEventListener('pg-click', () => this.adicionarBloco());
    this.root.querySelector('#novo-tipo-bloco')?.addEventListener('change', (evento) => {
      this.novoTipoBloco = (evento.target as HTMLSelectElement).value as BlocoConteudo['tipo'];
    });

    this.root.querySelectorAll('[data-remover-bloco]').forEach((el) => {
      el.addEventListener('click', () => this.removerBloco(Number(el.getAttribute('data-remover-bloco'))));
    });
    this.root.querySelectorAll('[data-mover-cima]').forEach((el) => {
      el.addEventListener('click', () => this.moverBloco(Number(el.getAttribute('data-mover-cima')), -1));
    });
    this.root.querySelectorAll('[data-mover-baixo]').forEach((el) => {
      el.addEventListener('click', () => this.moverBloco(Number(el.getAttribute('data-mover-baixo')), 1));
    });
    this.root.querySelectorAll('[data-restaurar]').forEach((el) => {
      el.addEventListener('click', () => void this.restaurar(el.getAttribute('data-restaurar')!));
    });
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

function escaparAtributo(valor: string): string {
  return escaparHtml(valor).replace(/"/g, '&quot;');
}

customElements.define('erp-pagina-editor-modal', ErpPaginaEditorModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-pagina-editor-modal': ErpPaginaEditorModal;
  }
}
